// PAWSWeekCard — surfaces the current week's expectations on Today.
// Collapsible so it doesn't dominate the screen for users who already
// know what to expect.

import { useState } from 'react';
import { Card } from '../ui/Card';
import type { PAWSWeek } from '../../content/pawsCalendar';

interface PAWSWeekCardProps {
  week: PAWSWeek;
  daysIn: number;
}

export function PAWSWeekCard({ week, daysIn }: PAWSWeekCardProps) {
  const [open, setOpen] = useState(false);
  return (
    <Card accentLeft>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        style={{
          inlineSize: '100%',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 'var(--space-3)',
          background: 'transparent',
          border: 0,
          padding: 0,
          cursor: 'pointer',
          textAlign: 'start',
        }}
        aria-expanded={open}
      >
        <div>
          <h3
            className="text-h3"
            style={{ marginBlockEnd: 'var(--space-1)', fontWeight: 500 }}
          >
            {week.title}
          </h3>
          <p className="muted text-caption" style={{ margin: 0 }}>
            Day {daysIn} of your taper · what often comes up this week
          </p>
        </div>
        <span
          aria-hidden="true"
          style={{
            fontSize: 'var(--text-body-lg)',
            color: 'var(--color-ink-tertiary)',
            transform: open ? 'rotate(90deg)' : 'none',
            transition: 'transform var(--dur-fast) var(--ease-out)',
            paddingInlineStart: 'var(--space-2)',
          }}
        >
          ›
        </span>
      </button>
      {open ? (
        <div style={{ marginBlockStart: 'var(--space-3)' }}>
          <p className="text-caption muted" style={{ marginBlockEnd: 'var(--space-1)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            What often comes up
          </p>
          <ul style={{ paddingInlineStart: 'var(--space-5)', margin: 0, marginBlockEnd: 'var(--space-3)' }}>
            {week.expected.map((e) => (
              <li key={e} style={{ color: 'var(--color-ink-secondary)', lineHeight: 1.5, marginBlockEnd: 'var(--space-1)' }}>{e}</li>
            ))}
          </ul>
          <p className="text-caption muted" style={{ marginBlockEnd: 'var(--space-1)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            What helps
          </p>
          <ul style={{ paddingInlineStart: 'var(--space-5)', margin: 0, marginBlockEnd: 'var(--space-3)' }}>
            {week.coping.map((c) => (
              <li key={c} style={{ color: 'var(--color-ink-secondary)', lineHeight: 1.5, marginBlockEnd: 'var(--space-1)' }}>{c}</li>
            ))}
          </ul>
          <p
            style={{
              fontFamily: 'var(--font-heading)',
              fontStyle: 'italic',
              color: 'var(--color-ink-primary)',
              margin: 0,
            }}
          >
            {week.reassurance}
          </p>
        </div>
      ) : null}
    </Card>
  );
}
